import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import {
    INIT_SUCCESS,
    LOADING_BALLOON_PAYMENT_GRID,
    PAYMENT_RECEIVED,
    RESET_BALLOON_PAYMENT_GRID_DATA,
    UPDATE_BALLOON_PAYMENT_GRID_AXIS_VALUES,
    UPDATE_BALLOON_PAYMENT_GRID_DATA,
    UPDATED_BALLOON_PAYMENT_GRID_TOGGLE,
    REVERT_BALLOON_DEAL_TO_BASE_FINANCE
} from '../types/actionTypes';
import type { BalloonPaymentState } from '../types/balloonTypes';

/**
 * Initial state for balloon payment reducer
 */
export const initialState: BalloonPaymentState = {
    currentBalloonPayment: null,
    balloonPaymentGridData: null,
    paymentBalloonGridToggle: false,
    balloonDownPayments: [],
    balloonTerms: []
};

/**
 * Balloon Payment Reducer
 * Handles state management for balloon payment deals
 *
 * @description Balloon payments are a type of finance deal where the borrower
 * makes smaller regular monthly payments with a large "balloon" payment due
 * at the end of the loan term.
 */
export const reducer = (state = initialState, action: AnyFSA): BalloonPaymentState => {
    switch (action.type) {
        /**
         * Handle payment response from Payment Services
         * Extracts balloon payment data from the finance response
         */
        case PAYMENT_RECEIVED: {
            // R-10 latest-call-wins: drop a superseded (stale) response so an out-of-order lease call
            // can't overwrite the fresh balloon result. Only leaseEndpointMiddleware sets this.
            if (action.meta?.isStalePaymentResponse) {
                return state;
            }
            const balloonPayment = action.payload?.finance?.balloonPayment;
            const balloonPaymentGridData = action.payload?.finance?.balloonPaymentGridData;

            // Return early if no balloon data in payload
            if (!balloonPayment && !balloonPaymentGridData?.length) {
                return state;
            }

            return {
                ...state,
                ...(balloonPayment && {
                    currentBalloonPayment: {
                        balloonTerm: balloonPayment.balloonTerm,
                        balloonType: balloonPayment.balloonType,
                        balloonAmount: balloonPayment.balloonAmount,
                        monthlyPayment: balloonPayment.monthlyPayment,
                        totalOfRegularPayments: balloonPayment.totalOfRegularPayments
                    }
                }),
                ...(balloonPaymentGridData?.length && { balloonPaymentGridData })
            };
        }

        /**
         * Update balloon payment grid data directly
         */
        case UPDATE_BALLOON_PAYMENT_GRID_DATA: {
            const payload = action.payload;

            if (!payload) return state;

            return { ...state, balloonPaymentGridData: payload };
        }

        /**
         * Initialize balloon payment grid with loading state
         */
        case LOADING_BALLOON_PAYMENT_GRID: {
            const { balloonDownPayments, balloonTerms } = action.payload;

            return {
                ...state,
                balloonDownPayments,
                balloonTerms
            };
        }

        /**
         * Update balloon payment grid axis values (terms, down payments)
         */
        case UPDATE_BALLOON_PAYMENT_GRID_AXIS_VALUES: {
            const { balloonDownPayments, balloonTerms } = action.payload;

            return {
                ...state,
                ...(balloonDownPayments && { balloonDownPayments }),
                ...(balloonTerms && { balloonTerms })
            };
        }

        /**
         * Toggle balloon payment grid visibility
         */
        case UPDATED_BALLOON_PAYMENT_GRID_TOGGLE: {
            return {
                ...state,
                paymentBalloonGridToggle: action.payload
            };
        }

        /**
         * Reset balloon payment grid data to initial state
         */
        case RESET_BALLOON_PAYMENT_GRID_DATA: {
            return {
                ...state,
                balloonPaymentGridData: null,
                paymentBalloonGridToggle: false
            };
        }

        /**
         * Revert balloon deal back to base finance
         * Clears all balloon-specific state
         */
        case REVERT_BALLOON_DEAL_TO_BASE_FINANCE: {
            return {
                ...initialState
            };
        }

        /**
         * Initialize balloon state from deal init response
         */
        case INIT_SUCCESS: {
            const balloonPayment = action.payload?.offer?.finance?.balloonPayment;

            if (balloonPayment) {
                return {
                    ...state,
                    currentBalloonPayment: {
                        balloonTerm: balloonPayment.balloonTerm,
                        balloonType: balloonPayment.balloonType,
                        balloonAmount: balloonPayment.balloonAmount,
                        monthlyPayment: balloonPayment.monthlyPayment,
                        totalOfRegularPayments: balloonPayment.totalOfRegularPayments
                    }
                };
            }

            return state;
        }

        default:
            return state;
    }
};

export default reducer;
